import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PaymentIcon from '@mui/icons-material/Payment';
import LanIcon from '@mui/icons-material/Lan';
import HubIcon from '@mui/icons-material/Hub';
import { useTranslation } from 'react-i18next';
import PavoP2pInlineSelector from './PavoP2pInlineSelector';

export interface FiscalPrinterSettings {
  fiscalPrinterType: 'huginT300' | 'inposM530' | 'pavopay' | 'other';
  huginT300BaseUrl?: string;
  inposM530BaseUrl?: string;
  // PavoPay ayarları
  pavoIpAddress?: string;
  pavoPort?: number;
  pavoSerialNumber?: string;
  pavoApplicationId?: string;
  /**
   * PavoPay alt-tipi:
   *  - 'rest' (varsayılan): Kiosk doğrudan POS'a HTTP REST API ile bağlanır
   *    (pavoIpAddress + pavoPort + pavoSerialNumber kullanılır).
   *  - 'rest_p2p': Ödeme isteği `pavo_rest_p2p` modundaki gts_pavo cihaza
   *    SignalR ile yönlendirilir; gts_pavo PavoPay POS ile P2P konuşur.
   *
   * Undefined/eksik ise 'rest' davranışı korunur (geriye uyum).
   */
  pavoIntegrationMode?: 'rest' | 'rest_p2p';
  /** REST-P2P seçildiğinde hedef gts_pavo cihazının `screenCode` değeri. */
  pavoP2pDeviceCode?: string;
  isEnabled: boolean;
}

interface KioskFiscalPrinterTabProps {
  settings: FiscalPrinterSettings;
  onChange: (settings: FiscalPrinterSettings) => void;
  /** PavoPay > REST-P2P altında pavo_rest_p2p modlu cihaz listesini çekmek için. */
  companyId?: string | null;
  /** Şube filtresi; opsiyonel — undefined geçerse company'nin tüm cihazları listelenir. */
  branchId?: string | null;
}

const KioskFiscalPrinterTab: React.FC<KioskFiscalPrinterTabProps> = ({ settings, onChange, companyId, branchId }) => {
  const { t } = useTranslation('salesSystem');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Yeni `pavoIntegrationMode` opsiyonel; eski kayıtlarda undefined gelebilir
  // → 'rest' davranışı (geriye uyum garantisi).
  const pavoMode: 'rest' | 'rest_p2p' = settings.pavoIntegrationMode ?? 'rest';

  const handleTypeChange = (event: SelectChangeEvent) => {
    const newType = event.target.value as FiscalPrinterSettings['fiscalPrinterType'];
    onChange({
      ...settings,
      fiscalPrinterType: newType,
      // PavoPay seçildiğinde varsayılan port + integrasyon modu ayarla
      ...(newType === 'pavopay'
        ? {
            pavoPort: settings.pavoPort || 4568,
            pavoIntegrationMode: settings.pavoIntegrationMode ?? 'rest',
          }
        : {}),
    });
    setTestResult(null);
  };

  const handlePavoModeChange = (_e: React.MouseEvent<HTMLElement>, newMode: 'rest' | 'rest_p2p' | null) => {
    if (!newMode) return; // ToggleButtonGroup deselect engelle
    if (newMode === 'rest_p2p') {
      // REST-P2P modunda IP/seri vs. anlamsız → enabled flag cihaz seçimine bağlı.
      onChange({
        ...settings,
        pavoIntegrationMode: 'rest_p2p',
        isEnabled: !!settings.pavoP2pDeviceCode,
      });
    } else {
      // REST modunda IP + seri numarası varsa enabled.
      onChange({
        ...settings,
        pavoIntegrationMode: 'rest',
        isEnabled: !!(settings.pavoIpAddress?.trim() && settings.pavoSerialNumber?.trim()),
      });
    }
    setTestResult(null);
  };

  const handlePavoP2pDeviceChange = (deviceCode: string) => {
    onChange({
      ...settings,
      pavoP2pDeviceCode: deviceCode,
      isEnabled: !!deviceCode,
    });
  };

  // Hugin T300 URL değişikliği
  const handleHuginUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...settings,
      huginT300BaseUrl: event.target.value,
      isEnabled: event.target.value.trim().length > 0,
    });
  };

  // Inpos M530 URL değişikliği
  const handleInposUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...settings,
      inposM530BaseUrl: event.target.value,
      isEnabled: event.target.value.trim().length > 0,
    });
  };

  // PavoPay alan değişiklikleri
  const handlePavoFieldChange = (field: string, value: string | number) => {
    const updated = { ...settings, [field]: value };
    // IP ve seri numarası doluysa enabled yap
    if (field === 'pavoIpAddress' || field === 'pavoSerialNumber') {
      updated.isEnabled = !!(updated.pavoIpAddress?.trim() && updated.pavoSerialNumber?.trim());
    }
    onChange(updated);
  };

  // Hugin/Inpos bağlantı testi
  const handleTestConnection = async () => {
    const baseUrl = settings.fiscalPrinterType === 'huginT300' 
      ? settings.huginT300BaseUrl 
      : settings.inposM530BaseUrl;

    if (!baseUrl) {
      setTestResult({ 
        success: false, 
        message: t('kioskFiscalTab.errors.enterUrl', {
          model: settings.fiscalPrinterType === 'huginT300' ? 'Hugin T300' : 'Inpos M530'
        })
      });
      return;
    }

    setTestLoading(true);
    setTestResult(null);

    try {
      let normalizedUrl = baseUrl.replace(/\/+$/, '');
      if (normalizedUrl.includes('/api/status')) {
        normalizedUrl = normalizedUrl.replace(/\/api\/status.*$/, '');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${normalizedUrl}/api/status`, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setTestResult({
          success: true,
          message: t('kioskFiscalTab.messages.connectionSuccess', { device: data.deviceName || settings.fiscalPrinterType }),
        });
        onChange({ ...settings, isEnabled: true });
      } else {
        setTestResult({
          success: false,
          message: t('kioskFiscalTab.errors.connectionWithStatus', { status: `${response.status} ${response.statusText}` }),
        });
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        setTestResult({ success: false, message: t('kioskFiscalTab.errors.timeout') });
      } else {
        setTestResult({ success: false, message: t('kioskFiscalTab.errors.connectionWithStatus', { status: error.message || t('kioskFiscalTab.errors.unknownError') }) });
      }
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PrintIcon /> {t('kioskFiscalTab.title')}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {t('kioskFiscalTab.subtitle')}
      </Typography>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {/* Yazarkasa Tipi Seçimi */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="fiscal-printer-type-label">{t('kioskFiscalTab.fields.model')}</InputLabel>
            <Select
              labelId="fiscal-printer-type-label"
              id="fiscal-printer-type"
              value={settings.fiscalPrinterType}
              label={t('kioskFiscalTab.fields.model')}
              onChange={handleTypeChange}
            >
              <MenuItem value="pavopay">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentIcon fontSize="small" />
                  PavoPay
                </Box>
              </MenuItem>
              <MenuItem value="huginT300">Hugin T300</MenuItem>
              <MenuItem value="inposM530">Inpos M530</MenuItem>
              <MenuItem value="other" disabled>
                {t('kioskFiscalTab.fields.otherComingSoon')}
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* ========== PavoPay Ayarları ========== */}
        {settings.fiscalPrinterType === 'pavopay' && (
          <>
            {/* PavoPay Entegrasyon Tipi (REST / REST-P2P) */}
            <Grid item xs={12}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {t('kioskFiscalTab.pavopay.integrationMode.label')}
              </Typography>
              <ToggleButtonGroup
                value={pavoMode}
                exclusive
                onChange={handlePavoModeChange}
                color="primary"
                size="small"
              >
                <ToggleButton value="rest" sx={{ textTransform: 'none', px: 2 }}>
                  <LanIcon fontSize="small" sx={{ mr: 1 }} />
                  {t('kioskFiscalTab.pavopay.integrationMode.rest')}
                </ToggleButton>
                <ToggleButton value="rest_p2p" sx={{ textTransform: 'none', px: 2 }}>
                  <HubIcon fontSize="small" sx={{ mr: 1 }} />
                  {t('kioskFiscalTab.pavopay.integrationMode.restP2p')}
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {pavoMode === 'rest_p2p'
                  ? t('kioskFiscalTab.pavopay.integrationMode.restP2pHint')
                  : t('kioskFiscalTab.pavopay.integrationMode.restHint')}
              </Typography>
            </Grid>

            {/* ===== REST (mevcut, doğrudan POS bağlantısı) ===== */}
            {pavoMode === 'rest' && (
              <>
                {/* POS IP Adresi */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('kioskFiscalTab.fields.posIpAddress')}
                    placeholder="192.168.1.100"
                    value={settings.pavoIpAddress || ''}
                    onChange={(e) => handlePavoFieldChange('pavoIpAddress', e.target.value)}
                    helperText={t('kioskFiscalTab.fields.posIpHelp')}
                  />
                </Grid>

                {/* Port */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('kioskFiscalTab.fields.port')}
                    value={settings.pavoPort || 4568}
                    onChange={(e) => handlePavoFieldChange('pavoPort', parseInt(e.target.value) || 4568)}
                    helperText={t('kioskFiscalTab.fields.portHelp')}
                    type="number"
                  />
                </Grid>

                {/* POS Seri Numarası */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('kioskFiscalTab.fields.posSerial')}
                    placeholder="N500Z000028"
                    value={settings.pavoSerialNumber || ''}
                    onChange={(e) => handlePavoFieldChange('pavoSerialNumber', e.target.value)}
                    helperText={t('kioskFiscalTab.fields.posSerialHelp')}
                  />
                </Grid>

                {/* Uygulama ID */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('kioskFiscalTab.fields.appIdOptional')}
                    placeholder="GTS-KIOSK-001"
                    value={settings.pavoApplicationId || ''}
                    onChange={(e) => handlePavoFieldChange('pavoApplicationId', e.target.value)}
                    helperText={t('kioskFiscalTab.fields.appIdHelp')}
                  />
                </Grid>
              </>
            )}

            {/* ===== REST-P2P (gts_pavo cihazı üzerinden) ===== */}
            {pavoMode === 'rest_p2p' && (
              <Grid item xs={12}>
                <PavoP2pInlineSelector
                  companyId={companyId ?? undefined}
                  branchId={branchId ?? undefined}
                  value={settings.pavoP2pDeviceCode}
                  onChange={handlePavoP2pDeviceChange}
                />
              </Grid>
            )}
          </>
        )}

        {/* ========== Hugin T300 Ayarları ========== */}
        {settings.fiscalPrinterType === 'huginT300' && (
          <>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('kioskFiscalTab.common.correctFormat')} <code>http://192.168.1.100:3003</code>
              </Alert>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Hugin T300 API Base URL"
                placeholder="http://192.168.1.100:3003"
                value={settings.huginT300BaseUrl || ''}
                onChange={handleHuginUrlChange}
                helperText={t('kioskFiscalTab.common.urlHelper')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={testLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                onClick={handleTestConnection}
                disabled={testLoading || !settings.huginT300BaseUrl}
                sx={{ height: '56px' }}
              >
                {testLoading ? t('kioskFiscalTab.actions.testing') : t('kioskFiscalTab.actions.testConnection')}
              </Button>
            </Grid>

            {testResult && (
              <Grid item xs={12}>
                <Alert
                  severity={testResult.success ? 'success' : 'error'}
                  icon={testResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
                >
                  {testResult.message}
                </Alert>
              </Grid>
            )}
          </>
        )}

        {/* ========== Inpos M530 Ayarları ========== */}
        {settings.fiscalPrinterType === 'inposM530' && (
          <>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('kioskFiscalTab.common.correctFormat')} <code>http://192.168.1.100:3003</code>
              </Alert>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Inpos M530 API Base URL"
                placeholder="http://192.168.1.100:3003"
                value={settings.inposM530BaseUrl || ''}
                onChange={handleInposUrlChange}
                helperText={t('kioskFiscalTab.common.urlHelper')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={testLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                onClick={handleTestConnection}
                disabled={testLoading || !settings.inposM530BaseUrl}
                sx={{ height: '56px' }}
              >
                {testLoading ? t('kioskFiscalTab.actions.testing') : t('kioskFiscalTab.actions.testConnection')}
              </Button>
            </Grid>

            {testResult && (
              <Grid item xs={12}>
                <Alert
                  severity={testResult.success ? 'success' : 'error'}
                  icon={testResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
                >
                  {testResult.message}
                </Alert>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Bilgilendirme - sadece Hugin/Inpos için */}
      {(settings.fiscalPrinterType === 'huginT300' || settings.fiscalPrinterType === 'inposM530') && (
        <Alert severity="warning">
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            {t('kioskFiscalTab.notes.title')}
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
            <li>{t('kioskFiscalTab.notes.optionalIntegration')}</li>
            <li>{t('kioskFiscalTab.notes.localApiRequired')}</li>
            <li>{t('kioskFiscalTab.notes.autoUseInApp')}</li>
            <li>{t('kioskFiscalTab.notes.checkUrlService')}</li>
            <li>{t('kioskFiscalTab.notes.inposCardOnly')}</li>
          </Typography>
        </Alert>
      )}

    </Paper>
  );
};

export default KioskFiscalPrinterTab;
